import { Box } from '@mui/material';
import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { PageTitle } from '../../../components/PageTitle';
import { useTranslation } from 'react-i18next';
import { Breadcrumbs } from '../../../components/Breadcrumbs';
import { Crumb } from '../../../components/Breadcrumbs/interfaces';
import { useGetClient } from '../../../reactQuery/clients';
import { enqueueSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import { LoadingLayover } from '../../../components/LoadingLayover';
import {
  ActivityLogTab,
  DetailsTab,
  TabControl,
} from '../../../components/tabs';
import { DetailsTabPanel } from './tabs/DetailsTabPanel';
import { ActivityLogTabPanel } from './tabs/ActivityLogTabPanel';
import { PlannedActivitiesTab } from './tabs/PlannedActivitiesTab';
import { PlannedActivitiesTabPanel } from './tabs/PlannedActivitiesTabPanel';
import { OpportunitiesTab } from '../../../components/tabs/tabs/OpportunitiesTab';
import { OpportunitiesTabPanel } from '../../../components/tabs/tab-panels/OpportunitiesTabPanel';
import { DATA_TABLE_STORAGE_KEYS } from '../../../constants';

export const ClientDetails: React.FC = (): JSX.Element => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const params = useParams();

  const [currentTab, setCurrentTab] = useState<number>(0);

  const clientId = Number(params.id);
  const crumbs: Crumb[] = [
    {
      title: t('breadcrumbs.overview'),
      link: '/clients',
    },
  ];

  const { data: client, isLoading, error } = useGetClient(clientId);

  const handleClientNotFound = (): void => {
    enqueueSnackbar(
      t('errors.resourceNotFound', {
        resource: 'client',
      }),
      {
        variant: 'error',
      }
    );
    navigate('/clients');
  };

  useEffect(() => {
    if (Number.isNaN(clientId)) {
      handleClientNotFound();
    } else if (error) {
      const axiosError = error as AxiosError;

      if (axiosError.response?.status === 404) {
        handleClientNotFound();
      } else {
        enqueueSnackbar(t('errors.somethingWentWrong'), {
          variant: 'error',
        });
      }
    }
  }, [clientId, error, navigate]);

  if (isLoading) {
    return <LoadingLayover />;
  }

  if (!client) {
    navigate('/clients');
    return <Box>Error</Box>;
  }

  return (
    <Fragment>
      <PageTitle title={t('clients:title')} space={'double'}>
        <Breadcrumbs crumbs={crumbs} current={client.name} />
      </PageTitle>

      <TabControl value={currentTab} onChange={(value) => setCurrentTab(value)}>
        <DetailsTab />
        <PlannedActivitiesTab />
        <ActivityLogTab />
        <OpportunitiesTab />
      </TabControl>

      <DetailsTabPanel value={currentTab} index={0} client={client} />
      <PlannedActivitiesTabPanel
        value={currentTab}
        index={1}
        clientId={client.id}
      />
      <ActivityLogTabPanel value={currentTab} index={2} clientId={client.id} />
      <OpportunitiesTabPanel
        value={currentTab}
        index={3}
        filter="client"
        id={client.id}
        storageKey={DATA_TABLE_STORAGE_KEYS.clientOpportunitiesOverview}
      />
    </Fragment>
  );
};
